import { createContext, useState, useEffect, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { FVP_CONFIG_EDIT_MODES } from '../../core/constants';
import { toast } from 'react-toastify';
import { flattenObject } from '../../utilities/helpers';
import { getFvpConfigForSite } from '../../api/fvpDoc';
import { initialFvpSiteConfig } from '../../utilities/initialConfigs';

export const FvpConfigContext = createContext();

const FvpProvider = ({ children }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const pathName = location.pathname;
	const { siteId } = useParams();

	//STATES
	const [fvpConfigEditMode, setfvpConfigEditMode] = useState(null);
	const previousFvpCouchBaseConfig = useRef({});
	const [fvpConfig, setfvpConfig] = useState(null); // Stores the FVP configuration as a key-value pair object
	const [curSelectedId, setCurSelectedId] = useState(''); // Stores the ID of the currently selected video section
	let rawConfig = fvpConfig?.ads[curSelectedId] ? fvpConfig.ads[curSelectedId] : {}; // Retrieves the configuration for the currently selected video section from fvpConfig.ads

	/* USE EFFECTS */
	useEffect(() => {
		if (pathName.includes('/fvp-editor/create-config')) {
			setfvpConfigEditMode(FVP_CONFIG_EDIT_MODES.CREATE);
			setfvpConfig(initialFvpSiteConfig);
			previousFvpCouchBaseConfig.current = initialFvpSiteConfig;
		} else if (pathName.includes('fvp-editor/update-config')) {
			fetchData();
		}
	}, []);

	async function fetchData() {
		try {
			const { data } = await getFvpConfigForSite(siteId);
			let fvpSiteConfig = data?.data?.fvpDoc;

			// Flatten each nested video section within the `ads` object to allow direct access to properties by their keys.
			if (fvpSiteConfig) {
				let videoSectionIds = Object.keys(fvpSiteConfig.ads);
				videoSectionIds.forEach((videoSectionId) => {
					const videoSectionObj = fvpSiteConfig.ads[videoSectionId];
					const flattenedVideoSectionObj = flattenObject(videoSectionObj);
					fvpSiteConfig.ads[videoSectionId] = flattenedVideoSectionObj;
				});
				setCurSelectedId(videoSectionIds[0]);
				setfvpConfig(fvpSiteConfig);
				previousFvpCouchBaseConfig.current = fvpSiteConfig;
				setfvpConfigEditMode(FVP_CONFIG_EDIT_MODES.UPDATE);
			}
		} catch (err) {
			const requestError = err?.response?.data?.data?.message || err.message;
			toast.error(requestError, { position: toast.POSITION.BOTTOM_RIGHT });
			navigate('/dashboard');
		}
	}

	const updateAds = (videoSectionId, data) => {
		const clonedFvpConfig = structuredClone(fvpConfig);
		const updatedData = {
			...clonedFvpConfig,
			ads: { ...clonedFvpConfig.ads, [videoSectionId]: data }
		};
		setfvpConfig(updatedData);
	};

	/**
	 * @name updateRawConfig
	 * @description Updates the `ads` object with new data.
	 *
	 * This function can handle various types of values for the flatten `ads` object, including strings, numbers, or arrays.
	 *
	 * @param {Object|function} ObjOrCallBack - An object containing updates for the `ads` object, or a callback function that returns such an object.
	 *
	 * @returns {void}
	 */
	const updateRawConfig = (ObjOrCallBack) => {
		if (typeof ObjOrCallBack === 'function') {
			const latestObj = ObjOrCallBack(rawConfig);
			updateAds(curSelectedId, latestObj);
			return;
		}
		updateAds(curSelectedId, ObjOrCallBack);
	};

	return (
		<FvpConfigContext.Provider
			value={{
				fvpConfig,
				updateFvpConfig: setfvpConfig,
				fvpConfigEditMode,
				updateFvpConfigMode: setfvpConfigEditMode,
				curSelectedId,
				updateCurSelectedId: setCurSelectedId,
				rawConfig,
				updateRawConfig,
				previousFvpCouchBaseConfig
			}}
		>
			{children}
		</FvpConfigContext.Provider>
	);
};
export default FvpProvider;
